import React, { useEffect, useState } from 'react';
import { api, apiErr } from '@/lib/api';
import { usePeriod } from '@/context/PeriodContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { Plus, Trash2, Edit3 } from 'lucide-react';
import PeriodPicker from '@/components/PeriodPicker';
import { fmtMXN, fmtDateMX } from '@/lib/format';

export default function Sales() {
  const { year, month } = usePeriod();
  const [period, setPeriod] = useState(null);
  const [activities, setActivities] = useState([]);
  const [sales, setSales] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ activity_id: '', date: '', total: '', invoiced: '', cfdi_global: '', iva_rate: '', notes: '', uuid_cfdi: '' });

  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    const p = await api.get(`/periods/lookup/${year}/${month}`);
    setPeriod(p.data);
    const a = await api.get('/config/activities');
    setActivities(a.data);
    const s = await api.get(`/sales?period_id=${p.data.id}`);
    setSales(s.data);
  };

  useEffect(() => { load(); }, [year, month]);

  const defaultActivity =
    activities.find((activity) => activity.id === 'refresqueria') ?? activities[0];

  const startNew = () => {
    setEditing(null);
    setForm({ activity_id: defaultActivity?.id || '', date: today, total: '', invoiced: '', cfdi_global: '', iva_rate: defaultActivity?.default_iva_rate ?? '', notes: '', uuid_cfdi: '' });
    setOpen(true);
  };

  const startEdit = (s) => {
    setEditing(s);
    setForm({ ...s, iva_rate: s.iva_rate ?? '' });
    setOpen(true);
  };

  const save = async () => {
    if (!form.activity_id || !form.date || !form.total) { toast.error('Completa actividad, fecha y total'); return; }
    const payload = {
      period_id: period.id,
      activity_id: form.activity_id,
      date: form.date,
      total: parseFloat(form.total || 0),
      invoiced: parseFloat(form.invoiced || 0),
      cfdi_global: parseFloat(form.cfdi_global || 0),
      iva_rate: form.iva_rate === '' ? null : parseFloat(form.iva_rate),
      notes: form.notes || '', uuid_cfdi: form.uuid_cfdi || '',
    };
    try {
      if (editing) await api.patch(`/sales/${editing.id}`, payload);
      else await api.post('/sales', payload);
      toast.success('Venta guardada');
      setOpen(false);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar venta?')) return;
    try { await api.delete(`/sales/${id}`); toast.success('Eliminada'); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <PeriodPicker/>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Ventas</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Registrar por mes en que fueron cobradas</p>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button data-testid="btn-add-sale" onClick={startNew} className="h-11 rounded-full px-5">
              <Plus className="h-4 w-4 mr-1"/> Nueva
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl max-h-[92vh] overflow-y-auto">
            <SheetHeader><SheetTitle className="font-display text-left">{editing ? 'Editar venta' : 'Nueva venta'}</SheetTitle></SheetHeader>
            <div className="space-y-4 mt-4 pb-8">
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Actividad</Label>
                <Select value={form.activity_id} onValueChange={(v) => {
                  const a = activities.find(x => x.id === v);
                  setForm(f => ({ ...f, activity_id: v, iva_rate: a?.default_iva_rate ?? f.iva_rate }));
                }}>
                  <SelectTrigger data-testid="sale-activity" className="h-12 mt-1"><SelectValue placeholder="Selecciona…"/></SelectTrigger>
                  <SelectContent>
                    {[...activities]
                      .sort((a, b) => {
                        if (a.id === 'refresqueria') return -1;
                        if (b.id === 'refresqueria') return 1;
                        return 0;
                      })
                      .map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)
                    }
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Fecha</Label>
                <Input type="date" data-testid="sale-date" className="h-12 mt-1" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Total ventas</Label>
                  <Input type="number" inputMode="decimal" data-testid="sale-total" className="h-12 mt-1 num" placeholder="0.00" value={form.total} onChange={(e) => setForm(f => ({ ...f, total: e.target.value }))}/>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Tasa IVA</Label>
                  <Input type="number" step="0.01" inputMode="decimal" data-testid="sale-iva-rate" className="h-12 mt-1 num" placeholder="0.16" value={form.iva_rate} onChange={(e) => setForm(f => ({ ...f, iva_rate: e.target.value }))}/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Facturado individual</Label>
                  <Input type="number" inputMode="decimal" data-testid="sale-invoiced" className="h-12 mt-1 num" placeholder="0.00" value={form.invoiced} onChange={(e) => setForm(f => ({ ...f, invoiced: e.target.value }))}/>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">CFDI global</Label>
                  <Input type="number" inputMode="decimal" data-testid="sale-cfdi" className="h-12 mt-1 num" placeholder="0.00" value={form.cfdi_global} onChange={(e) => setForm(f => ({ ...f, cfdi_global: e.target.value }))}/>
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">UUID / Folio CFDI</Label>
                <Input data-testid="sale-uuid" className="h-12 mt-1" placeholder="Opcional" value={form.uuid_cfdi} onChange={(e) => setForm(f => ({ ...f, uuid_cfdi: e.target.value }))}/>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Notas</Label>
                <Textarea data-testid="sale-notes" className="mt-1" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}/>
              </div>
              <Button data-testid="sale-save" onClick={save} className="w-full h-12 rounded-full">Guardar</Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {sales.length === 0 ? (
        <Card className="p-8 text-center border-dashed"><p className="text-sm text-muted-foreground">Sin ventas registradas este mes.</p></Card>
      ) : (
        <div className="space-y-2" data-testid="sales-list">
          {[...sales]
          .sort((a, b) => String(b.date).localeCompare(String(a.date)))
          .map((s) => {
            const a = activities.find(x => x.id === s.activity_id);
            return (
              <Card key={s.id} className="p-4 border-border rounded-lg" data-testid={`sale-item-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm">{a?.name || s.activity_id}</p>
                    <p className="text-xs text-muted-foreground">{fmtDateMX(s.date)} · IVA {(s.iva_rate*100).toFixed(2)}%</p>
                    <div className="text-xs text-muted-foreground mt-1">
                      Fact.: <span className="num">{fmtMXN(s.invoiced)}</span> · CFDI global: <span className="num">{fmtMXN(s.cfdi_global)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-lg font-bold num">{fmtMXN(s.total)}</p>
                    <div className="flex gap-1 justify-end mt-1">
                      <button data-testid={`sale-edit-${s.id}`} onClick={() => startEdit(s)} className="p-1.5 rounded hover:bg-secondary"><Edit3 className="h-3.5 w-3.5"/></button>
                      <button data-testid={`sale-delete-${s.id}`} onClick={() => remove(s.id)} className="p-1.5 rounded hover:bg-secondary text-destructive"><Trash2 className="h-3.5 w-3.5"/></button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground pt-2">Los depósitos bancarios no se consideran automáticamente como ventas.</p>
    </div>
  );
}
